'use client'

import { useEffect, useRef } from 'react'
import { useOptimizedCartStore } from '@/stores/optimized-cart-store'
import { createClient } from '@/app/[locale]/utils/supabase/client'

/**
 * Global component to handle Zustand store hydration with optimistic updates
 * This should be included in the root layout to ensure stores are hydrated on all pages
 */
export default function StoreHydration() {
  const hasInitialized = useRef(false);
  const supabase = createClient();

  useEffect(() => {
    if (hasInitialized.current) return;

    // Initialize optimized cart store with optimistic updates
    const initializeOptimizedCart = async () => {
      try {
        const { syncCart, clearError } = useOptimizedCartStore.getState();

        // Clear any previous errors and pending operations
        clearError();

        // Sync cart with optimistic updates enabled
        syncCart();

      } catch (error) {
        console.error('[StoreHydration] Error initializing optimized cart:', error);
        const { setError } = useOptimizedCartStore.getState();
        setError('Failed to initialize cart');
      }
    };

    // Initialize the optimized cart
    initializeOptimizedCart();

    // Mark initialization as done
    hasInitialized.current = true;
  }, []);

  // Listen for auth state changes to handle sign-in and sign-out with optimistic updates
  useEffect(() => {
    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event) => {

      if (event === 'SIGNED_IN') {
        // When user signs in, sync cart with optimistic updates
        try {
          const { syncCart, clearError, clearPendingOperations } = useOptimizedCartStore.getState();

          // Clear any previous errors and pending operations
          clearError();
          clearPendingOperations();

          // Force a cart sync with optimistic updates
          syncCart();

        } catch (error) {
          console.error('[StoreHydration] Error syncing cart after sign in:', error);
          useOptimizedCartStore.getState().setError('Failed to sync cart after sign in');
        }
      } else if (event === 'SIGNED_OUT') {
        // Clear pending operations on sign out
        const { clearPendingOperations, clearError } = useOptimizedCartStore.getState();
        clearPendingOperations();
        clearError();
      }
    });

    return () => {
      // Clean up the subscription
      subscription.unsubscribe();
    };
  }, [supabase]);

  // Add a separate effect to handle sign-out cookie detection with optimistic updates
  useEffect(() => {
    // Function to check for the clear cart data cookie
    const checkForClearDataSignal = () => {
      // Check for cookies
      const cookies = document.cookie.split(';');
      const clearCartCookie = cookies.find(cookie => cookie.trim().startsWith('clear_cart_data='));

      // Check for header (useful for initial page load after sign-out)
      const urlParams = new URLSearchParams(window.location.search);
      const clearCartParam = urlParams.get('clear_cart_data');

      const shouldClearData = clearCartCookie || clearCartParam === 'true';

      if (shouldClearData) {

        // Clear optimized cart store with optimistic updates
        const { clearCart, clearPendingOperations, clearError } = useOptimizedCartStore.getState();

        clearCart();
        clearPendingOperations();
        clearError();

        // Remove the cookies once processed
        if (clearCartCookie) {
          document.cookie = 'clear_cart_data=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; sameSite=strict;';
        }

        // Remove the URL parameter if possible (without causing a page reload)
        if (clearCartParam === 'true' && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('clear_cart_data');
          window.history.replaceState({}, document.title, url.toString());
        }
      }
    };

    // Run on component mount
    checkForClearDataSignal();

    // Also listen for storage events (for cross-tab synchronization with optimistic updates)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'signout_event') {
        checkForClearDataSignal();
      } else if (event.key === 'optimized-cart-store') {
        // Handle cross-tab cart synchronization
        try {
          const { forceSync } = useOptimizedCartStore.getState();
          forceSync();
        } catch (error) {
          console.error('[StoreHydration] Error syncing cart across tabs:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // This component doesn't render anything
  return null;
} 